import { enUS } from "@mui/x-data-grid/locales";

import { LANGUAGES } from "@/constants/language";
import type { DataTableState } from "@/constants/types";
import { useTranslation } from "@/hooks/useTranslation";

import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import {
	Box,
	FormControl,
	MenuItem,
	Pagination,
	PaginationItem,
	Paper,
	Select,
	Typography,
} from "@mui/material";
import type { SelectChangeEvent } from "@mui/material/Select";
import { styled } from "@mui/material/styles";
import {
	DataGrid,
	type DataGridProps,
	type GridAutosizeOptions,
	type GridColDef,
	type GridFilterModel,
	type GridSortModel,
	gridPageCountSelector,
	gridPageSelector,
	gridPageSizeSelector,
	gridRowCountSelector,
	useGridApiContext,
	useGridApiRef,
	useGridSelector,
} from "@mui/x-data-grid";
import { useEffect, useMemo, useRef } from "react";

import { ErrorComponent } from "./error";
import {
	PAGE_SIZE_OPTIONS,
	toSafePageSize,
	useDataTablePaginationModel,
} from "./useDataTablePaginationModel";

const StyledPaginationItem = styled(PaginationItem)(({ theme }) => ({
	height: 34,
	minWidth: 34,
	marginInline: 4, // a bit more breathing room
	borderRadius: 10,
	border: `1px solid ${theme.palette.divider}`,
	background: "transparent",
	color: theme.palette.text.primary,

	"&:hover": {
		background: theme.palette.action.hover,
	},

	"&.Mui-selected": {
		borderColor: theme.palette.primary.main,
		background: theme.palette.primary.main,
		color: theme.palette.primary.contrastText,
	},

	"&.Mui-selected:hover": {
		background: theme.palette.primary.dark,
	},

	"&.MuiPaginationItem-ellipsis": {
		border: "none",
		background: "transparent",
	},
}));

export function DataTablePagination() {
	const apiRef = useGridApiContext();
	const page = useGridSelector(apiRef, gridPageSelector); // 0-based
	const pageCount = useGridSelector(apiRef, gridPageCountSelector);
	const pageSizeFromGrid = useGridSelector(apiRef, gridPageSizeSelector);
	const rowCount = useGridSelector(apiRef, gridRowCountSelector);

	const pageSize = toSafePageSize(Number(pageSizeFromGrid));

	const { t } = useTranslation();

	const from = rowCount > 0 ? page * pageSize + 1 : 0;
	const to = rowCount > 0 ? Math.min((page + 1) * pageSize, rowCount) : 0;

	const handlePageChange = (_: React.ChangeEvent<unknown>, value: number) => {
		apiRef.current.setPage(value - 1);
	};

	const handlePageSizeChange = (e: SelectChangeEvent<number>) => {
		const nextRaw = Number(e.target.value);
		const next = toSafePageSize(nextRaw);
		apiRef.current.setPaginationModel({ page: 0, pageSize: next });
	};

	return (
		<Box
			sx={{
				display: "flex",
				alignItems: "center",
				width: "100%",
				px: 4, // more horizontal padding
				py: 5, // more vertical padding
				gap: 3, // more spacing between left/center/right
			}}
		>
			{/* Left */}
			<Box
				sx={{ flex: 1, display: "flex", justifyContent: "flex-start", pl: 0.5 }}
			>
				<Typography
					variant="body2"
					sx={{ color: "text.secondary", whiteSpace: "nowrap" }}
				>
					{rowCount >= 0
						? `${from}-${to} ${t("pagination.of")} ${rowCount} ${t(
								"pagination.items",
							)}`
						: `${t("pagination.page")} ${page + 1} ${t(
								"pagination.of",
							)} ${pageCount}`}
				</Typography>
			</Box>

			{/* Center */}
			<Box sx={{ display: "flex", justifyContent: "center", px: 1 }}>
				<Pagination
					page={page + 1}
					count={pageCount}
					onChange={handlePageChange}
					siblingCount={2}
					boundaryCount={2}
					showFirstButton={false}
					showLastButton={false}
					sx={{
						"& .MuiPagination-ul": {
							alignItems: "center",
							gap: 0.25, // subtle extra spacing between items
						},
					}}
					renderItem={(item) => (
						<StyledPaginationItem
							{...item}
							slots={{
								previous: ChevronLeftIcon,
								next: ChevronRightIcon,
							}}
						/>
					)}
				/>
			</Box>

			{/* Right */}
			<Box
				sx={{ flex: 1, display: "flex", justifyContent: "flex-end", pr: 0.5 }}
			>
				<Box sx={{ display: "flex", alignItems: "center", gap: 1.25 }}>
					<Typography
						variant="body2"
						sx={{ color: "text.secondary", whiteSpace: "nowrap" }}
					>
						{t("pagination.perPage")}
					</Typography>

					<FormControl size="small">
						<Select<number>
							value={pageSize}
							onChange={handlePageSizeChange}
							sx={{
								minWidth: 88,
								height: 34,
								borderRadius: 2,
								backgroundColor: "rgba(255,255,255,0.04)",
								"& .MuiOutlinedInput-notchedOutline": {
									borderColor: "rgba(140, 190, 255, 0.16)",
								},
								"&:hover .MuiOutlinedInput-notchedOutline": {
									borderColor: "rgba(140, 190, 255, 0.32)",
								},
								"&.Mui-focused .MuiOutlinedInput-notchedOutline": {
									borderColor: "rgba(60, 200, 255, 0.95)",
								},
							}}
						>
							{PAGE_SIZE_OPTIONS.map((n) => (
								<MenuItem key={n} value={n}>
									{n}
								</MenuItem>
							))}
						</Select>
					</FormControl>
				</Box>
			</Box>
		</Box>
	);
}

const StyledDataGrid = styled(DataGrid)(({ theme }) => ({
	borderRadius: theme.spacing(3),

	"& .MuiDataGrid-columnHeaders .MuiDataGrid-row--borderBottom": {
		backgroundColor: theme.palette.background.paper,
		"& .MuiDataGrid-columnHeader": {
			borderBottom: "2px solid",
			borderBottomColor: theme.palette.background.default,
			paddingInline: theme.spacing(5),

			"& .MuiDataGrid-columnHeaderTitle": {
				...theme.typography.body1,
			},
		},
		"& .MuiDataGrid-filler": {
			borderBottom: "2px solid",
			borderBottomColor: theme.palette.background.default,
		},
	},

	"&& .MuiDataGrid-iconButtonContainer .MuiIconButton-root": {
		boxShadow: "none",
	},

	"& .MuiDataGrid-row .MuiDataGrid-cell": {
		borderBottom: "2px solid",
		borderBottomColor: theme.palette.background.default,
		...theme.typography.body2,
		paddingInline: theme.spacing(5),
	},
	"& .MuiDataGrid-cell:focus, & .MuiDataGrid-cell:focus-within": {
		outline: "none",
	},
	"& .MuiDataGrid-columnHeader:focus, & .MuiDataGrid-columnHeader:focus-within":
		{
			outline: "none",
		},

	"& .MuiDataGrid-scrollbar": {
		scrollbarWidth: "auto",
		marginBlock: theme.spacing(4),
		width: "100%",
		scrollbarColor: `${theme.palette.grey[400]} ${theme.palette.background.paper}`,
		position: "static",
	},
}));

export interface DataTableProps
	extends Omit<
		DataGridProps,
		| "rows"
		| "columns"
		| "rowCount"
		| "loading"
		| "paginationModel"
		| "filterModel"
		| "sortModel"
		| "onPaginationModelChange"
		| "onFilterModelChange"
		| "onSortModelChange"
		| "rowHeight"
		| "columnHeaderHeight"
	> {
	columns: GridColDef[];
	rows: any[];
	rowCount: number;
	loading: boolean;
	rowHeight?: number;
	columnHeaderHeight?: number;
	error: Error | null;

	state: DataTableState;
	onStateChange: (newState: DataTableState) => void;
	onErrorReset?: () => void;
}

const DataTable: React.FC<DataTableProps> = (props) => {
	const {
		columns: userColumns,
		rows,
		rowCount,
		loading,
		rowHeight = 55,
		columnHeaderHeight = 55,
		state,
		error = null,
		onStateChange,
		onErrorReset,
		...rest
	} = props;
	const apiRef = useGridApiRef();
	const autosizeOptions: GridAutosizeOptions = {
		includeHeaders: true,
		includeOutliers: true,
		expand: true,
	};

	const { lang } = useTranslation();
	const columns = [...userColumns];

	const handleErrorReset = onErrorReset || (() => window.location.reload());

	const { paginationModel, handlePaginationModelChange } =
		useDataTablePaginationModel({
			state,
			onStateChange,
		});

	const handleFilterModelChange = (filterModel: GridFilterModel) => {
		onStateChange({ ...state, filterModel });
	};

	const handleSortModelChange = (sortModel: GridSortModel) => {
		onStateChange({ ...state, sortModel });
	};

	useEffect(() => {
		`${rowCount}`;
		if (apiRef.current) {
			setTimeout(() => {
				apiRef.current.autosizeColumns(autosizeOptions);
			});
		}
	}, [rowCount, apiRef]);

	useEffect(() => {
		`${columns.length}-${rows.length}`;
		if (apiRef.current) {
			apiRef.current.autosizeColumns(autosizeOptions);
		}
	}, [columns, rows, apiRef]);
	const rowCountRef = useRef(rowCount || 0);
	const rowCountNumber = useMemo(() => {
		if (rowCountRef.current !== undefined && !loading) {
			rowCountRef.current = rowCount || 0;
		}
		return rowCountRef.current;
	}, [rowCount, loading]);

	if (error) {
		return (
			<Box sx={{ height: "auto" }}>
				<ErrorComponent error={error} reset={handleErrorReset} />
			</Box>
		);
	}

	return (
		<Paper sx={{ p: 0, boxShadow: 0, width: "100%", height: "100%" }}>
			<StyledDataGrid
				apiRef={apiRef}
				{...rest}
				rows={rows}
				columns={columns}
				rowCount={rowCountNumber}
				loading={loading}
				paginationModel={paginationModel}
				filterModel={state.filterModel}
				sortModel={state.sortModel}
				paginationMode="server"
				filterMode="server"
				sortingMode="server"
				onPaginationModelChange={handlePaginationModelChange}
				onFilterModelChange={handleFilterModelChange}
				onSortModelChange={handleSortModelChange}
				disableColumnSelector
				disableRowSelectionOnClick
				disableColumnMenu
				disableColumnFilter
				disableVirtualization
				localeText={
					LANGUAGES[lang].dataTableLocale ||
					enUS.components.MuiDataGrid.defaultProps.localeText
				}
				slots={{ pagination: DataTablePagination }}
				pageSizeOptions={[...PAGE_SIZE_OPTIONS]}
				autosizeOptions={autosizeOptions}
				autosizeOnMount
				slotProps={{
					...props.slotProps,
					loadingOverlay: {
						variant: "linear-progress",
						noRowsVariant: "skeleton",
					},
				}}
				rowHeight={rowHeight}
				columnHeaderHeight={columnHeaderHeight}
			/>
		</Paper>
	);
};

export default DataTable;
